import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Unlink, Network } from 'lucide-react';
const SILOS = [{
  id: 'jira-hubspot',
  tool1: 'jira',
  tool2: 'hubspot',
  text: <>
        <span className="font-semibold text-[#2B211A]" data-id="element-1988" data-mp="18l0zjtaifq2b">Jira's AI</span> can
        summarize your Jira tickets. It cannot tell you which Jira tickets are
        related to the{' '}
        <span className="font-semibold text-[#2B211A]" data-id="element-1989" data-mp="1iip7xm11gw2y">HubSpot</span> deals that
        are at risk this quarter.
      </>
}, {
  id: 'slack-linear',
  tool1: 'slack',
  tool2: 'linear',
  text: <>
        <span className="font-semibold text-[#2B211A]" data-id="element-1990" data-mp="1s8ihkhhmf14k">Slack's AI</span> can
        summarize your Slack threads. It cannot tell you which Slack
        conversations are relevant to the sprint that is behind schedule in{' '}
        <span className="font-semibold text-[#2B211A]" data-id="element-1991" data-mp="218f0s3l8r7s8">Linear</span>.
      </>
}, {
  id: 'notion-zendesk',
  tool1: 'notion',
  tool2: 'zendesk',
  text: <>
        <span className="font-semibold text-[#2B211A]" data-id="element-1992" data-mp="1w80uapzag5wb">Notion's AI</span> can
        draft content inside Notion. It cannot tell you which Notion pages are
        relevant to the support ticket that just came in from your top account.
      </>
}];
export function CapabilityGrid() {
  return <section className="w-full bg-[#FAF8F4] py-24 md:py-32 border-t border-neutral-200/70 overflow-hidden" data-id="element-1993" data-mp="36gxtz1e66ndr">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-1994" data-mp="3bf93tc4gsxvz">
        {/* Header — left-aligned on mobile, centered from the sm breakpoint up. */}
        <div className="max-w-4xl mx-auto text-left sm:text-center mb-16 md:mb-20" data-id="element-1995" data-mp="3kh8jo13qtvxc">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6
        }} className="text-4xl md:text-5xl lg:text-6xl font-title font-medium text-[#2B211A] tracking-tight leading-[1.05] mb-6" data-id="element-1996" data-mp="3p873hanxxc5c">
            AI adoption fails without <br className="hidden sm:block" data-id="element-1997" data-mp="242yn5w2rbeni" />
            <span className="text-[#EA580C]" data-id="element-1998" data-mp="24401qaizej9z">an AI brain</span>
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="text-lg md:text-xl text-neutral-500 leading-relaxed max-w-3xl mx-auto" data-id="element-1999" data-mp="1z6891eaiit4q">
            Every major SaaS platform has added AI features in the last two
            years. The problem is that each platform's AI only knows about that
            platform's data.
          </motion.p>
        </div>

        {/* Silo Examples */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto mb-16 md:mb-24" data-id="element-2000" data-mp="2rr81kcxn504m">
          {SILOS.map((silo, i) => <motion.div key={silo.id} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.5,
          delay: 0.2 + i * 0.1
        }} className="bg-white rounded-3xl p-6 md:p-8 border border-neutral-200/60 shadow-[0_8px_30px_-12px_rgba(43,33,26,0.06)] flex flex-col" data-id="element-2001" data-mp="xfb9qivads91" data-mpi={i}>
              <div className="flex items-center gap-3 mb-6" data-id="element-2002" data-mp="ijd1hptunivv">
                <div className="w-12 h-12 rounded-2xl bg-[#FAF8F4] border border-neutral-200/80 flex items-center justify-center shrink-0" data-id="element-2003" data-mp="3mvsv6d1oid0">
                  <img src={`https://cdn.simpleicons.org/${silo.tool1}/2B211A`} alt="" className="w-6 h-6 object-contain opacity-70" loading="lazy" onError={e => {
                const img = e.currentTarget as HTMLImageElement;
                if (!img.dataset.fallback) {
                  img.dataset.fallback = '1';
                  img.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${silo.tool1}.svg`;
                  // The dashboard-icons fallback is full-color (e.g. Slack,
                  // which Simple Icons no longer serves). Force it to the
                  // same dark monochrome silhouette as every other mark so
                  // the row stays visually consistent.
                  img.style.filter = 'brightness(0)';
                } else {
                  img.style.visibility = 'hidden';
                }
              }} data-id="element-2004" data-mp="8o1nxau6ghpp" />
                </div>
                <div className="flex-1 h-px bg-gradient-to-r from-neutral-200 via-neutral-300 to-neutral-200 relative" data-id="element-2005" data-mp="375ohiwkv001h">
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-2" data-id="element-2006" data-mp="32baimk6znas2">
                    <Unlink size={16} className="text-neutral-300" data-id="element-2007" data-mp="2xbxt1roqfpls" />
                  </div>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-[#FAF8F4] border border-neutral-200/80 flex items-center justify-center shrink-0" data-id="element-2008" data-mp="2odcgqe9ut6tg">
                  <img src={`https://cdn.simpleicons.org/${silo.tool2}/2B211A`} alt="" className="w-6 h-6 object-contain opacity-70" loading="lazy" onError={e => {
                const img = e.currentTarget as HTMLImageElement;
                if (!img.dataset.fallback) {
                  img.dataset.fallback = '1';
                  img.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${silo.tool2}.svg`;
                  // Force the full-color fallback to the same dark
                  // monochrome silhouette as the rest of the row.
                  img.style.filter = 'brightness(0)';
                } else {
                  img.style.visibility = 'hidden';
                }
              }} data-id="element-2009" data-mp="2jfu5uica0hit" />
                </div>
              </div>
              <p className="text-neutral-600 leading-relaxed text-sm md:text-base" data-id="element-2010" data-mp="txxa8iis1f9d">
                {silo.text}
              </p>
            </motion.div>)}
        </div>

        {/* Payoff */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="max-w-6xl mx-auto bg-[#1A1410] rounded-[2rem] p-8 md:p-16 relative overflow-hidden shadow-2xl" data-id="element-2011" data-mp="1gtbnbg157ii">
          {/* Subtle background glow */}
          <div className="absolute inset-0 pointer-events-none" style={{
          background: 'radial-gradient(circle at 50% 0%, rgba(234,88,12,0.15) 0%, transparent 70%)'
        }} data-id="element-2012" data-mp="19i71wz5bb32q" />

          <div className="relative z-10 flex flex-col md:flex-row gap-10 md:gap-16 items-start" data-id="element-2013" data-mp="gub75zzazl8l">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#EA580C] to-[#C2410C] flex items-center justify-center shrink-0 shadow-lg shadow-[#EA580C]/20" data-id="element-2014" data-mp="utkmyyjlkdc1">
              <Network className="text-white w-8 h-8" data-id="element-2015" data-mp="pwu8ugrcrrt9" />
            </div>
            <div className="flex-1 text-left" data-id="element-2016" data-mp="267ohq6y0e5i5">
              <h3 className="text-2xl md:text-4xl font-title font-medium text-white mb-5 tracking-tight leading-[1.1]" data-id="element-2017" data-mp="219wkcxelhr6k">
                When every tool has its own AI, you just have a collection of
                silos.
              </h3>
              <p className="text-neutral-400 text-base md:text-lg leading-relaxed max-w-3xl" data-id="element-2018" data-mp="2dtjxhwho6dnu">
                An AI brain for your business is not a feature inside a tool. It
                is a layer that sits above all your tools and connects them. It
                needs to know about Jira, Slack, Notion, and HubSpot
                simultaneously—answering questions and taking actions across all
                of them from a single interface.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}